import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from 'primereact/calendar';
import { Badge } from 'primereact/badge';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

// Components
import CustomDataTable from '../../components/DataTable';
import Loader from '../../components/Loader';

// Actions
import { getInterviewTechnology } from '../../redux/actions/hr';

// APIs
import {
  getAllCallReportApi,
  getInterviewTechnologyApi,
  filteredHrListApi
} from '../../redux/apis/hr';

// Constants & Utilities
import { CALLSTATUS_MAP, CALL_STATUS_CONFIG, DEBOUNCE_TIMEOUT } from '../../constants/common';
import { renderHrAvatar, isDarkMode } from '../../utils/common';
import { debounce } from '../../utils/debounce';

const DATE_PILLS = ['Today', 'This Week', 'This Month', 'Custom Range'];

/**
 * Extracts and formats query parameters for API calls
 */
const getFilterParams = (activePill, customRange, techFilter, statusFilter, hrFilter) => {
  const durationMap = {
    Today: 'today',
    'This Week': 'week',
    'This Month': 'month',
    'Custom Range': 'custom'
  };
  const duration = durationMap[activePill];

  let startDate = null;
  let endDate = null;
  if (duration === 'custom') {
    const range = customRange && customRange[0] ? customRange : [new Date(), new Date()];
    startDate = dayjs(range[0]).format('YYYY-MM-DD');
    endDate = range[1] ? dayjs(range[1]).format('YYYY-MM-DD') : startDate;
  }

  const technology = techFilter?.length > 0 ? techFilter.join(',') : undefined;
  const status = statusFilter?.length > 0 ? statusFilter.join(',') : undefined;
  const hr = hrFilter?.length > 0 ? hrFilter.join(',') : undefined;

  return { duration, startDate, endDate, technology, status, hr };
};

/**
 * Visual badge for Call Status
 */
const CallStatusBadge = ({ status }) => {
  if (status === undefined || status === null || status === '') return <span>--</span>;

  const statusCode = Number(status);
  const statusConfig = CALL_STATUS_CONFIG.find(
    (c) => c.code === statusCode || c.enumKey === status || c.label === status
  );
  const label = statusConfig?.label || CALLSTATUS_MAP[statusCode] || status;
  const color = statusConfig?.color || '#6c757d';

  return (
    <span
      className="rpt-call-status-pill"
      style={{
        backgroundColor: `${color}18`,
        color: color,
        borderColor: `${color}40`
      }}>
      <span className="rpt-call-status-pill__dot" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
};

const AdminCallReport = () => {
  const dispatch = useDispatch();

  // DOM Refs for popups / overlays
  const inlineCalendarRef = useRef(null);
  const stickyCalendarRef = useRef(null);
  const inlineMultiSelectRef = useRef(null);
  const stickyMultiSelectRef = useRef(null);
  const inlineStatusSelectRef = useRef(null);
  const stickyStatusSelectRef = useRef(null);
  const inlineHrMultiSelectRef = useRef(null);
  const stickyHrMultiSelectRef = useRef(null);

  // Redux Store & Local Technology List
  const technologyListData = useSelector((state) => state.hrs.interviewTechnologyType);
  const [localTechList, setLocalTechList] = useState([]);

  // HR Recruiters Options
  const [hrOptions, setHrOptions] = useState([]);

  // Filter States
  const [activePill, setActivePill] = useState('This Month');
  const [customRange, setCustomRange] = useState([new Date(), new Date()]);

  // Technology Filter (immediate visual vs debounced API trigger)
  const [techFilter, setTechFilter] = useState([]);
  const [techFilterPending, setTechFilterPending] = useState([]);

  // Status Filter (immediate visual vs debounced API trigger)
  const [statusFilter, setStatusFilter] = useState([]);
  const [statusFilterPending, setStatusFilterPending] = useState([]);

  // HR Recruiter Filter (immediate visual vs debounced API trigger)
  const [hrFilter, setHrFilter] = useState([]);
  const [hrFilterPending, setHrFilterPending] = useState([]);

  // UI state
  const [showStickyFilter, setShowStickyFilter] = useState(false);
  const [loading, setLoading] = useState(false);

  // Pagination & Data States
  const [callLogsData, setCallLogsData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);

  // Debounced setters for filter changes
  const debouncedSetTechFilter = useMemo(
    () =>
      debounce((value) => {
        setTechFilter(value);
        setPage(1);
      }, DEBOUNCE_TIMEOUT),
    []
  );

  const debouncedSetStatusFilter = useMemo(
    () =>
      debounce((value) => {
        setStatusFilter(value);
        setPage(1);
      }, DEBOUNCE_TIMEOUT),
    []
  );

  const debouncedSetHrFilter = useMemo(
    () =>
      debounce((value) => {
        setHrFilter(value);
        setPage(1);
      }, DEBOUNCE_TIMEOUT),
    []
  );

  // Cleanup pending debounced calls on unmount
  useEffect(() => {
    return () => {
      debouncedSetTechFilter.cancel?.();
      debouncedSetStatusFilter.cancel?.();
      debouncedSetHrFilter.cancel?.();
    };
  }, [debouncedSetTechFilter, debouncedSetStatusFilter, debouncedSetHrFilter]);

  const handleTechFilterChange = (e) => {
    setTechFilterPending(e.value);
    debouncedSetTechFilter(e.value);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilterPending(e.value);
    debouncedSetStatusFilter(e.value);
  };

  const handleHrFilterChange = (e) => {
    setHrFilterPending(e.value);
    debouncedSetHrFilter(e.value);
  };

  // Combine redux technology list and local technology list
  const effectiveTechnologies = useMemo(() => {
    if (technologyListData && Array.isArray(technologyListData) && technologyListData.length > 0) {
      return technologyListData;
    }
    return localTechList || [];
  }, [technologyListData, localTechList]);

  // Technology Options
  const techOptions = useMemo(() => {
    if (!effectiveTechnologies || !Array.isArray(effectiveTechnologies)) return [];
    return effectiveTechnologies.map((tech) => ({
      label: tech.Technology || tech.name || tech.label || '',
      value: tech.ID !== undefined ? tech.ID : tech.id !== undefined ? tech.id : tech.value
    }));
  }, [effectiveTechnologies]);

  // Status Options from CALL_STATUS_CONFIG
  const statusOptions = useMemo(() => {
    return CALL_STATUS_CONFIG.map((item) => ({
      label: item.label,
      value: item.code
    }));
  }, []);

  // Fetch Technology List and HR Recruiters on component mount
  useEffect(() => {
    dispatch(getInterviewTechnology());
    getInterviewTechnologyApi()
      .then((res) => {
        if (res?.data?.data && Array.isArray(res.data.data)) {
          setLocalTechList(res.data.data);
        }
      })
      .catch((err) => {
        console.error('Error fetching technology list directly:', err);
      });

    const fetchHRs = async () => {
      try {
        const response = await filteredHrListApi();
        if (response?.status === 200) {
          const sortedHrs = [...(response.data.data || [])].sort((a, b) => {
            if (a.isActive && !b.isActive) return -1;
            if (!a.isActive && b.isActive) return 1;
            return 0;
          });
          const list = sortedHrs.map((hr) => ({
            label: `${hr.FirstName} ${hr.LastName}${hr.isActive ? '' : ' (Inactive)'}`,
            value: hr.ID,
            isActive: hr.isActive
          }));
          setHrOptions(list);
        }
      } catch (err) {
        console.error('Error fetching HR recruiters:', err);
        toast.error('Error fetching HR recruiters');
      }
    };
    fetchHRs();
  }, [dispatch]);

  // ═════════════════════════════════════════════════════════════════
  // ── CALL REPORT DATA FETCH ────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════
  useEffect(() => {
    const fetchCallReport = async () => {
      setLoading(true);
      const params = getFilterParams(activePill, customRange, techFilter, statusFilter, hrFilter);
      if (!params) {
        setLoading(false);
        return;
      }

      try {
        const response = await getAllCallReportApi({
          duration: params.duration,
          startDate: params.startDate,
          endDate: params.endDate,
          hr: params.hr,
          technology: params.technology,
          status: params.status,
          page,
          size
        });

        if (response?.status === 200 || response?.data?.code === 200) {
          const resData = response.data?.data || {};
          setCallLogsData(resData.data || []);
          setTotalRecords(resData.pagination?.totalRecords || 0);
        }
      } catch (error) {
        console.error('Error fetching call report data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCallReport();
  }, [activePill, customRange, techFilter, statusFilter, hrFilter, page, size]);

  // Floating sticky filter bar handling on scroll
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyFilter(window.scrollY > 80);
      [inlineCalendarRef, stickyCalendarRef].forEach((ref) => {
        if (ref.current) {
          if (typeof ref.current.hideOverlay === 'function') ref.current.hideOverlay();
          else if (typeof ref.current.hide === 'function') ref.current.hide();
        }
      });
      [
        inlineMultiSelectRef,
        stickyMultiSelectRef,
        inlineStatusSelectRef,
        stickyStatusSelectRef,
        inlineHrMultiSelectRef,
        stickyHrMultiSelectRef
      ].forEach((ref) => {
        if (ref.current) {
          if (typeof ref.current.hideOverlay === 'function') ref.current.hideOverlay();
          else if (typeof ref.current.hide === 'function') ref.current.hide();
        }
      });
      if (
        document.activeElement &&
        (document.activeElement.closest('.p-calendar') ||
          document.activeElement.closest('.p-multiselect'))
      ) {
        document.activeElement.blur();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Helper to render technology colored badges
  const renderTechnologyBadges = (row) => {
    if (row.technologies && Array.isArray(row.technologies) && row.technologies.length > 0) {
      return (
        <ul className="technology-list">
          {row.technologies.map((tech) => (
            <li
              key={tech.id || tech.ID || tech.name}
              style={{
                backgroundColor: isDarkMode()
                  ? tech.darkModeColorCode || tech.colorCode || '#6c757d'
                  : tech.colorCode || '#6c757d'
              }}
              className="technology-badge">
              {tech.name || tech.Technology}
            </li>
          ))}
        </ul>
      );
    }

    if (row.technology) {
      const techItems =
        typeof row.technology === 'string' ? row.technology.split(',') : [row.technology];
      return (
        <ul className="technology-list">
          {techItems.map((techName, idx) => (
            <li key={idx} className="technology-badge technology-badge--default">
              {String(techName).trim()}
            </li>
          ))}
        </ul>
      );
    }

    return <span>--</span>;
  };

  // Helper to render HR Recruiter column
  const renderHrColumn = (row) => {
    if (!row.hrName && !row.hrId) return <span>--</span>;
    const nameParts = (row.hrName || '').split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    return (
      <div className="flex align-items-center gap-2">
        {row.hrId ? renderHrAvatar(row.hrId, row.hrProfile, firstName, lastName, false) : null}
      </div>
    );
  };

  // Filter Bar Renderer (Static & Sticky)
  const renderFilterBar = (isSticky = false) => (
    <div
      className={`rpt-filter-bar ${isSticky ? 'rpt-filter-bar--sticky' : ''} ${
        isSticky && showStickyFilter ? 'rpt-filter-bar--sticky-show' : ''
      }`}>
      {/* ── Date Range Pills ── */}
      <div className="rpt-pill-group">
        {DATE_PILLS.map((pill) => (
          <button
            key={pill}
            type="button"
            className={`rpt-pill ${activePill === pill ? 'rpt-pill--active' : ''}`}
            onClick={() => {
              setActivePill(pill);
              setPage(1);
            }}>
            {pill}
          </button>
        ))}
      </div>

      {/* ── Custom Range Calendar ── */}
      {activePill === 'Custom Range' && (
        <Calendar
          ref={isSticky ? stickyCalendarRef : inlineCalendarRef}
          value={customRange}
          onChange={(e) => {
            setCustomRange(e.value || [new Date(), new Date()]);
            setPage(1);
          }}
          onHide={() => {
            if (customRange && customRange[0] && !customRange[1]) {
              setCustomRange([customRange[0], customRange[0]]);
            }
          }}
          selectionMode="range"
          readOnlyInput
          placeholder="Select date range"
          className="rpt-calendar"
          dateFormat="dd/mm/yy"
          showIcon
        />
      )}

      {/* ── Technology Filter ── */}
      <MultiSelect
        ref={isSticky ? stickyMultiSelectRef : inlineMultiSelectRef}
        value={techFilterPending}
        options={techOptions}
        optionLabel="label"
        optionValue="value"
        onChange={handleTechFilterChange}
        placeholder="Filter by Technology"
        display="chip"
        filter
        maxSelectedLabels={2}
        className="rpt-multiselect"
      />

      {/* ── HR Recruiter Filter ── */}
      <MultiSelect
        ref={isSticky ? stickyHrMultiSelectRef : inlineHrMultiSelectRef}
        value={hrFilterPending}
        options={hrOptions}
        onChange={handleHrFilterChange}
        placeholder="Filter by HR Recruiter"
        display="chip"
        filter
        optionLabel="label"
        optionValue="value"
        maxSelectedLabels={2}
        className="rpt-multiselect"
        itemTemplate={(option) => (
          <div
            className={`flex align-items-center ${!option.isActive ? 'inactive-hr-item' : ''}`}
            style={{ position: 'relative', paddingRight: '14px', width: '100%' }}>
            {option.label}
            <span
              className={`hr-status-dot ${option.isActive ? 'active-dot' : 'inactive-dot'}`}
              style={{ position: 'absolute', right: 0, top: '5px' }}></span>
          </div>
        )}
      />

      {/* ── Status Filter ── */}
      <MultiSelect
        ref={isSticky ? stickyStatusSelectRef : inlineStatusSelectRef}
        value={statusFilterPending}
        options={statusOptions}
        optionLabel="label"
        optionValue="value"
        onChange={handleStatusFilterChange}
        placeholder="Filter by Status"
        display="chip"
        filter
        maxSelectedLabels={2}
        className="rpt-multiselect"
      />
    </div>
  );

  return (
    <div className="rpt-call-report">
      {loading && <Loader />}

      {/* ── Static Filter Bar ── */}
      {renderFilterBar(false)}

      {/* ── Floating Sticky Filter Bar (Active on Scroll) ── */}
      {renderFilterBar(true)}

      {/* ── Table Card ── */}
      <div className="rpt-tabs-card rpt-tabs-card--padded">
        <div className="rpt-table-header-block">
          <div className="rpt-table-header-block__left">
            <i className="pi pi-list rpt-table-header-block__icon" />
            <span className="rpt-table-header-block__title">Call Details</span>
            {totalRecords > 0 && <Badge value={totalRecords} severity="info" className="ml-2" />}
          </div>
        </div>

        {/* ── Reusable CustomDataTable ── */}
        <CustomDataTable
          data={callLogsData}
          columns={[
            {
              field: 'date',
              header: 'Date',
              body: (row) => (
                <span className="white-space-nowrap">
                  {row.date ? dayjs(row.date).format('DD/MM/YYYY') : '--'}
                </span>
              )
            },
            {
              field: 'hrName',
              header: 'Recruiter',
              body: renderHrColumn
            },
            {
              field: 'technology',
              header: 'Technology',
              body: renderTechnologyBadges
            },
            {
              field: 'candidateName',
              header: 'Candidate Name',
              body: (row) => <span>{row.candidateName || row.name || '--'}</span>
            },
            {
              field: 'callStatus',
              header: 'Call Status',
              body: (row) => <CallStatusBadge status={row.callStatus} />
            }
          ]}
          lazy={true}
          isHeaderDisable={true}
          totalRecords={totalRecords}
          pageNumber={page}
          pageSize={size}
          onPagination={(p, s) => {
            setPage(p);
            setSize(s);
          }}
          className="rpt-datatable"
          emptyMessage="No call records found"
        />
      </div>
    </div>
  );
};

export default AdminCallReport;
